// The walk's path drawn on the ground in the 360° view, ahead of the camera
// and behind it (Phelipe 2026-10-22: "a line to go forward or backwards") -
// the way DroneDeploy draws its walk line (Phelipe 2026-09-25: "a light
// pink/red line showing the path"). Built from the walk's route (a raw .insv's
// GPS track, [[seconds, lat, lon], ...]) and assuming the camera faced the way
// it was carried: the video's centre is the direction of travel. GPS is good
// to a few metres, so the line shows the direction and the turns ahead, not
// the exact footing.
//
// The pure maths comes first (test/javascript/walk_path_test.mjs), then
// WalkPathOverlay, which draws on a canvas laid over PanoramaVideo's.

export const CAMERA_HEIGHT = 1.8 // metres: a 360° camera on a stick
// A raw 360° file is treated as filmed backwards (Phelipe 2026-09-25: "pretend
// we are going backwards in the raw 360 insta file"): the camera's front lens
// faced the person carrying it, so the way they walked is behind the video's
// centre. Set false to put the path in front of the centre again.
export const FILMED_BACKWARD = true

// Where the 360° view starts, in degrees: turned half round for a file filmed
// backwards, so it opens looking the way they walked (Phelipe 2026-09-25:
// "start the view with viewing backwards").
// "Flip direction" (K10-4, 2026-10-23) opens it the other way round.
export function startYaw(walk) {
  return Boolean(walk?.filmed_backward) !== Boolean(walk?.flipped) ? 180 : 0
}
// Which way the 360° view looks, as a compass bearing in degrees clockwise
// from north (Phelipe 2026-09-25, #5: "which way I'm facing" on the map):
// the way they walked (`heading`, radians) turned by how far the view is
// turned from looking that way. The view's yaw (radians) grows to the LEFT
// (the shader turns the ray counter-clockwise), and looking the way they
// walked is startYaw. null without a heading.
export function viewBearing(heading, yaw, walk) {
  if (heading == null || !Number.isFinite(heading) || !Number.isFinite(yaw)) return null
  const degrees = (heading * 180) / Math.PI + startYaw(walk) - (yaw * 180) / Math.PI
  return ((degrees % 360) + 360) % 360
}

// How wide the view is from side to side, in degrees: PanoramaVideo's fov is
// the top-to-bottom one.
export function horizontalFov(fov, width, height) {
  if (!(fov > 0) || !(width > 0) || !(height > 0)) return null
  return (2 * Math.atan(Math.tan((fov * Math.PI) / 360) * (width / height)) * 180) / Math.PI
}
export const AHEAD_METRES = 40 // how far ahead the line reaches
const NEAREST_METRES = 1.5 // closer than this, the ground is under the camera
const RIBBON_METRES = 0.35 // how wide the line is on the ground
// Mostly see-through (Phelipe 2026-09-25: "like 25%", after 50%): the ground shows through it.
const LINE = "rgba(251, 113, 133, 0.25)" // rose-400, the same as the map's path
const EDGE = "rgba(255, 255, 255, 0.14)"
const M_PER_DEG_LAT = 111132
const M_PER_DEG_LON = 111320

// Metres east and north of `origin` - both route points [t, lat, lon].
export function localMetres([, lat, lon], [, lat0, lon0]) {
  return {
    east: (lon - lon0) * M_PER_DEG_LON * Math.cos((lat0 * Math.PI) / 180),
    north: (lat - lat0) * M_PER_DEG_LAT
  }
}

// The route point k, averaged with its neighbours so one jumpy fix doesn't
// kink the line.
export function smoothedPoint(route, k) {
  const from = Math.max(0, k - 1)
  const to = Math.min(route.length - 1, k + 1)
  let lat = 0
  let lon = 0
  for (let j = from; j <= to; j++) {
    lat += route[j][1]
    lon += route[j][2]
  }
  const n = to - from + 1
  return [route[k][0], lat / n, lon / n]
}

// Which way the walker was heading at route point i, in radians clockwise from
// north: from where they were over the few seconds before to where they were
// over the few after. null when they were standing still.
export function headingAt(route, i, span = 4) {
  if (!Array.isArray(route) || route.length < 2 || i < 0 || i >= route.length) return null
  const mean = (from, to) => {
    let lat = 0
    let lon = 0
    for (let j = from; j <= to; j++) {
      lat += route[j][1]
      lon += route[j][2]
    }
    const n = to - from + 1
    return [0, lat / n, lon / n]
  }
  const before = mean(Math.max(0, i - span), i)
  const after = mean(i, Math.min(route.length - 1, i + span))
  const { east, north } = localMetres(after, before)
  if (Math.hypot(east, north) < 0.5) return null
  return Math.atan2(east, north)
}

// The path ahead of route point i as points on the ground, in the 360° view's
// own space (x to the right, y up, looking down -z), up to `ahead` metres
// along the way - or, with `behind`, the way they came, back along the route
// for as far - behind the video's centre when the file was filmed
// backwards (turned half round: x and z change sign). `facing` (radians,
// compass) is where the middle of the picture really faced, when the
// camera's own record says (#6, SiteWalkSteady) - then that, not the
// assumption. Each keeps its route index, so a click on it can walk there.
export function groundPoints(route, i, heading, { ahead = AHEAD_METRES, height = CAMERA_HEIGHT, backward = FILMED_BACKWARD, facing = null, behind = false } = {}) {
  const out = []
  if (!Array.isArray(route) || i < 0 || i >= route.length || heading == null) return out
  const origin = smoothedPoint(route, i)
  const cos = Math.cos(heading)
  const sin = Math.sin(heading)
  // Which way the middle of the picture faced: as recorded, or the way they
  // walked (turned half round for a file filmed backwards).
  const middle = Number.isFinite(facing) ? facing : heading + (backward ? Math.PI : 0)
  const mc = Math.cos(middle)
  const ms = Math.sin(middle)
  let along = 0
  let previous = origin
  const step = behind ? -1 : 1
  for (let k = i + step; k >= 0 && k < route.length && along <= ahead; k += step) {
    const point = smoothedPoint(route, k)
    const moved = localMetres(point, previous)
    along += Math.hypot(moved.east, moved.north)
    previous = point
    const { east, north } = localMetres(point, origin)
    // Ahead along the walk (behind it, for the way back), or it's under the
    // camera (or on the other side of it).
    if ((north * cos + east * sin) * step < NEAREST_METRES) continue
    const forward = north * mc + east * ms
    const right = east * mc - north * ms
    out.push({ x: right, y: -height, z: -forward, index: k })
  }
  return out
}

// The line stops before it nears the horizon (Kloepfer K10-6, Larry's Oct 20
// call: "like flying into space sometimes"). It is drawn as if the ground were
// flat and the camera level, so on a slope, or in a picture the camera's own
// record couldn't turn level, the far end (40 m ahead is only ~2.5° below the
// horizon) can land above the real horizon and the line climbs into the sky.
// Kept at least this far below it: about 20 m ahead of a camera on a stick.
export const MIN_BELOW_HORIZON_DEG = 5

// How far below the level horizon a ground point is, seen from the camera.
export function belowHorizonDeg({ x, y, z }) {
  return (Math.atan2(-y, Math.hypot(x, z)) * 180) / Math.PI
}

// The ground points that make it onto the canvas, in order: it stops where the
// path nears the horizon or goes behind the viewer.
export function pathOnScreen(points, view) {
  const shown = []
  for (const point of points) {
    if (belowHorizonDeg(point) < MIN_BELOW_HORIZON_DEG) break
    const at = projectToScreen(point, view)
    if (!at) break // the path went behind the viewer: stop there
    shown.push({ ...at, index: point.index })
  }
  return shown
}

// Where a point of the 360° view's space lands on the canvas for the view
// PanoramaVideo is drawing - the exact inverse of its shader (which tilts the
// ray, then turns it) - or null when it's behind the viewer. { x, y } in
// canvas pixels, and its depth in metres along the view.
export function projectToScreen({ x, y, z }, { yaw, pitch, fov, width, height }) {
  const cy = Math.cos(yaw)
  const sy = Math.sin(yaw)
  const x1 = x * cy - z * sy // undo the turn...
  const z1 = x * sy + z * cy
  const cp = Math.cos(pitch)
  const sp = Math.sin(pitch)
  const y2 = y * cp + z1 * sp // ...then the tilt
  const z2 = -y * sp + z1 * cp
  if (z2 > -1e-3) return null
  const t = Math.tan((fov * Math.PI) / 360)
  const clipX = x1 / -z2 / (t * (width / height))
  const clipY = y2 / -z2 / t
  return { x: ((clipX + 1) / 2) * width, y: ((1 - clipY) / 2) * height, depth: -z2 }
}

// A direction the 360° view looked (yaw and pitch in degrees, as
// PanoramaVideo keeps them) as a point 10 m away in the view's own space -
// where a saved view's pin goes (#8).
export function viewDirection(yawDeg, pitchDeg) {
  const yaw = (Number(yawDeg) * Math.PI) / 180
  const pitch = (Number(pitchDeg) * Math.PI) / 180
  return { x: -10 * Math.cos(pitch) * Math.sin(yaw), y: 10 * Math.sin(pitch), z: -10 * Math.cos(pitch) * Math.cos(yaw) }
}

// The route index of the drawn path nearest (x, y), within `tolerance` pixels
// of it, or -1. `drawn` is the path as drawn: [{ x, y, index }].
export function hitIndex(drawn, x, y, tolerance = 24) {
  let best = -1
  let bestDistance = tolerance
  for (let j = 0; j < drawn.length; j++) {
    const a = drawn[j]
    const b = drawn[j + 1] || a
    const dx = b.x - a.x
    const dy = b.y - a.y
    const length = dx * dx + dy * dy
    const along = length ? Math.max(0, Math.min(1, ((x - a.x) * dx + (y - a.y) * dy) / length)) : 0
    const distance = Math.hypot(a.x + along * dx - x, a.y + along * dy - y)
    if (distance <= bestDistance) {
      bestDistance = distance
      best = along < 0.5 ? a.index : b.index
    }
  }
  return best
}

// Draws the path ahead on a canvas laid over the 360° view, every frame the
// view is drawn.
export class WalkPathOverlay {
  constructor(canvas) {
    this.canvas = canvas
    this.drawn = []
    this.drawnBehind = []
    this.pins = []
    this.heading = null
  }

  clear() {
    const ctx = this.canvas.getContext?.("2d")
    ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.drawn = []
    this.drawnBehind = []
    this.pins = []
    this.heading = null
  }

  // A fresh, empty canvas the size of the view, before the path and pins.
  prepare(view) {
    const canvas = this.canvas
    const ctx = canvas.getContext?.("2d")
    if (!ctx || !view) return null
    if (canvas.width !== view.width || canvas.height !== view.height) {
      canvas.width = view.width
      canvas.height = view.height
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    this.drawn = []
    this.drawnBehind = []
    this.pins = []
    return ctx
  }

  // Saved views of this moment as pins where they looked (#8): orange with a
  // "!" for one that became an action item, blue for a saved view.
  // `pins`: [{ id, yaw, pitch, item }]. Drawn over the path, kept for clicks.
  drawPins(pins, view) {
    const ctx = this.canvas.getContext?.("2d")
    if (!ctx || !view || !pins?.length) return
    const r = 11 * view.dpr
    for (const pin of pins) {
      const at = projectToScreen(viewDirection(pin.yaw, pin.pitch), view)
      if (!at || at.x < -r || at.y < -r || at.x > view.width + r || at.y > view.height + r) continue
      ctx.beginPath()
      ctx.arc(at.x, at.y, r, 0, Math.PI * 2)
      ctx.fillStyle = pin.item ? "#f97316" : "#2563eb"
      ctx.fill()
      ctx.lineWidth = 2.5 * view.dpr
      ctx.strokeStyle = "#ffffff"
      ctx.stroke()
      ctx.fillStyle = "#ffffff"
      if (pin.item) {
        ctx.font = `bold ${Math.round(14 * view.dpr)}px sans-serif`
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText("!", at.x, at.y + 0.5 * view.dpr)
      } else {
        ctx.beginPath()
        ctx.arc(at.x, at.y, 3.5 * view.dpr, 0, Math.PI * 2)
        ctx.fill()
      }
      this.pins.push({ x: at.x / view.dpr, y: at.y / view.dpr, id: pin.id })
    }
  }

  // The pin under (x, y) in CSS pixels, or null.
  hitPin(x, y, tolerance = 16) {
    let best = null
    let bestDistance = tolerance
    for (const pin of this.pins) {
      const distance = Math.hypot(pin.x - x, pin.y - y)
      if (distance <= bestDistance) {
        bestDistance = distance
        best = pin.id
      }
    }
    return best
  }

  // `view` is what PanoramaVideo drew: yaw, pitch, fov, width and height in
  // device pixels, dpr. `backward`: the walk's file was filmed backwards.
  draw(route, index, view, { backward = FILMED_BACKWARD, facing = null } = {}) {
    const ctx = this.prepare(view)
    if (!ctx) return

    // Standing still has no heading: keep the last one rather than lose the line.
    const heading = headingAt(route, index)
    if (heading != null) this.heading = heading
    const t = Math.tan((view.fov * Math.PI) / 360)
    const widthAt = (depth) => Math.max(1.5, Math.min(18 * view.dpr, (RIBBON_METRES / depth / t) * (view.height / 2)))
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    // The way ahead and the way they came, each with its arrow; a click on
    // either walks there. Kept in CSS pixels, the way clicks arrive.
    const css = (shown) => shown.map((p) => ({ x: p.x / view.dpr, y: p.y / view.dpr, index: p.index }))
    for (const behind of [false, true]) {
      const shown = pathOnScreen(groundPoints(route, index, this.heading, { backward, facing, behind }), view)
      if (shown.length < 2) continue
      this._line(ctx, shown, widthAt, view)
      this._chevron(ctx, shown, widthAt)
      if (behind) this.drawnBehind = css(shown)
      else this.drawn = css(shown)
    }
  }

  _line(ctx, shown, widthAt, view) {
    for (const [colour, extra] of [[EDGE, 3 * view.dpr], [LINE, 0]]) {
      ctx.strokeStyle = colour
      for (let j = 1; j < shown.length; j++) {
        ctx.lineWidth = widthAt((shown[j - 1].depth + shown[j].depth) / 2) + extra
        ctx.beginPath()
        ctx.moveTo(shown[j - 1].x, shown[j - 1].y)
        ctx.lineTo(shown[j].x, shown[j].y)
        ctx.stroke()
      }
    }
  }

  // The route index under (x, y) on the line ahead, else on the line behind.
  hit(x, y) {
    const ahead = hitIndex(this.drawn, x, y)
    return ahead >= 0 ? ahead : hitIndex(this.drawnBehind, x, y)
  }

  // An arrow a few metres ahead, pointing along the path.
  _chevron(ctx, shown, widthAt) {
    const j = Math.max(1, shown.findIndex((p) => p.depth >= 5))
    const a = shown[j - 1]
    const b = shown[j]
    if (!a || !b) return
    const angle = Math.atan2(b.y - a.y, b.x - a.x)
    const size = widthAt(b.depth) * 1.6
    ctx.save()
    ctx.translate(b.x, b.y)
    ctx.rotate(angle)
    ctx.fillStyle = "rgba(255, 255, 255, 0.35)"
    ctx.beginPath()
    ctx.moveTo(size, 0)
    ctx.lineTo(-size * 0.6, -size * 0.75)
    ctx.lineTo(-size * 0.25, 0)
    ctx.lineTo(-size * 0.6, size * 0.75)
    ctx.closePath()
    ctx.fill()
    ctx.restore()
  }
}
